import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plane, Train, Users, Briefcase, Clock, Check } from 'lucide-react';
import { useI18n } from '../i18n';
import { Button } from '../components/ui/button';
import HoverVideoCard from '../components/HoverVideoCard';

const Disneyland = () => {
  const { t, lang } = useI18n();
  return (
    <div className="pt-[72px] bg-white">
      {/* HERO */}
      <section className="relative bg-black text-white py-24">
        <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1598961336118-93106a3cab26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxEaXNuZXlsYW5kJTIwUGFyaXMlMjBjYXN0bGV8ZW58MHx8fHwxNzc4MTQxOTY5fDA&ixlib=rb-4.1.0&q=85')" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.4em] uppercase text-white/60">
            {lang === 'fr' ? 'Destination magique' : 'Magical destination'}
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 max-w-3xl leading-[1.05]">Disneyland Paris</h1>
          <p className="text-white/80 mt-5 max-w-2xl text-lg">
            {lang === 'fr'
              ? 'Transferts priv\u00e9s vers Disneyland Paris depuis l\u2019a\u00e9roport Charles de Gaulle, Orly ou Paris. Service haut de gamme pour familles et VIP.'
              : 'Private transfers to Disneyland Paris from CDG, Orly or Paris. Premium service for families and VIPs.'}
          </p>
          <div className="mt-8 flex items-end gap-4">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/60">{lang === 'fr' ? '\u00c0 partir de' : 'From'}</p>
              <p className="font-serif text-6xl md:text-7xl mt-1">85 €</p>
            </div>
            <span className="pb-3 text-white/70 text-sm">{lang === 'fr' ? '\u2022 par trajet \u2022 forfait' : '\u2022 per trip \u2022 flat rate'}</span>
          </div>
        </div>
      </section>

      {/* VIDEO + PRICING */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div className="aspect-[16/10] overflow-hidden bg-black">
            <HoverVideoCard
              image="https://images.unsplash.com/photo-1598961336118-93106a3cab26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxEaXNuZXlsYW5kJTIwUGFyaXMlMjBjYXN0bGV8ZW58MHx8fHwxNzc4MTQxOTY5fDA&ixlib=rb-4.1.0&q=85"
              youtubeId="Lc2K1srGvbk"
              alt="Disneyland Paris"
            />
          </div>
          <div>
            <h2 className="font-serif text-4xl md:text-5xl">{lang === 'fr' ? 'Nos forfaits' : 'Our flat rates'}</h2>
            <p className="text-black/60 mt-3">
              {lang === 'fr'
                ? 'Tarif tout compris : carburant, p\u00e9ages, attente offerte 60 min, suivi de vol.'
                : 'All-inclusive: fuel, tolls, 60 min wait, flight tracking.'}
            </p>
            <div className="mt-8 divide-y divide-black/10 border-y border-black/10">
              {[
                { label: lang === 'fr' ? 'Standard' : 'Standard', sub: lang === 'fr' ? 'Max 4 pax \u2022 4 bagages' : 'Max 4 pax \u2022 4 bags', price: '85 €' },
                { label: lang === 'fr' ? 'Van' : 'Van', sub: lang === 'fr' ? 'Max 8 pax \u2022 10 bagages' : 'Max 8 pax \u2022 10 bags', price: '129 €' },
                { label: lang === 'fr' ? 'Berline' : 'Sedan', sub: lang === 'fr' ? 'Max 2 pax \u2022 4 bagages \u2022 sur devis' : 'Max 2 pax \u2022 4 bags \u2022 on quote', price: lang === 'fr' ? 'Devis' : 'Quote' },
                { label: lang === 'fr' ? 'Groupes (Sprinter, Minibus, Autocar)' : 'Groups (Sprinter, Minibus, Coach)', sub: lang === 'fr' ? 'Jusqu\u2019\u00e0 60 places' : 'Up to 60 seats', price: lang === 'fr' ? 'Devis' : 'Quote' }
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between py-4">
                  <div>
                    <p className="font-medium">{r.label}</p>
                    <p className="text-xs text-black/50 mt-0.5">{r.sub}</p>
                  </div>
                  <p className="font-serif text-2xl">{r.price}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-black hover:bg-black/85 text-white rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">
                  {lang === 'fr' ? 'R\u00e9server' : 'Book now'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:+33751284124">
                <Button variant="outline" className="border-black text-black hover:bg-black hover:text-white rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">
                  {lang === 'fr' ? 'Appeler' : 'Call'}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ROUTES */}
      <section className="py-20 bg-[#f6f4f1]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl md:text-5xl">{lang === 'fr' ? 'Itin\u00e9raires courants' : 'Common routes'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { icon: Plane, from: 'CDG', to: 'Disneyland Paris', sub: '~50 km \u2022 45 min', price: '85 €' },
              { icon: Plane, from: 'Orly', to: 'Disneyland Paris', sub: '~55 km \u2022 1 h', price: '85 €' },
              { icon: Train, from: 'Paris', to: 'Disneyland Paris', sub: '~32 km \u2022 40 min', price: '85 €' }
            ].map((r, i) => (
              <div key={i} className="bg-white p-6 border border-black/10">
                <r.icon className="w-5 h-5" />
                <p className="font-serif text-2xl mt-4">{r.from} <span className="text-black/40">\u2192</span> {r.to}</p>
                <p className="text-xs text-black/50 tracking-widest uppercase mt-2">{r.sub}</p>
                <p className="font-serif text-3xl mt-4">{r.price}</p>
                <p className="text-[10px] tracking-widest uppercase text-black/50 mt-1">{lang === 'fr' ? 'Standard \u2022 forfait' : 'Standard \u2022 flat rate'}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[11px] tracking-[0.4em] uppercase text-black/50">
            {lang === 'fr' ? 'Galerie' : 'Gallery'}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl mt-3">
            {lang === 'fr' ? 'Le parc en images' : 'The park in pictures'}
          </h2>
          <div className="mt-10 overflow-hidden border border-black/10 bg-[#f6f4f1]">
            <img
              src="/images/disneyland-collage.png"
              alt="Disneyland Paris"
              loading="lazy"
              className="w-full h-auto block"
              data-testid="disneyland-gallery-image"
            />
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl md:text-5xl">{lang === 'fr' ? 'Tout est inclus' : 'Everything included'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {[
              lang === 'fr' ? 'Si\u00e8ges enfants sur demande (gratuit)' : 'Child seats on request (free)',
              lang === 'fr' ? 'Accueil personnalis\u00e9 \u00e0 l\u2019a\u00e9roport' : 'Personalised airport meet & greet',
              lang === 'fr' ? 'Suivi de vol en temps r\u00e9el' : 'Real-time flight tracking',
              lang === 'fr' ? 'Attente offerte 60 minutes' : '60 minutes complimentary waiting',
              lang === 'fr' ? 'P\u00e9ages et carburant inclus' : 'Tolls & fuel included',
              lang === 'fr' ? 'Chauffeur professionnel anglophone' : 'English-speaking professional chauffeur'
            ].map((p, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-black/10">
                <Check className="w-5 h-5 mt-0.5 text-black" />
                <p className="text-black/80">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">{lang === 'fr' ? 'Pr\u00eat pour la magie ?' : 'Ready for the magic?'}</h2>
            <p className="text-white/70 mt-2">{lang === 'fr' ? 'R\u00e9servation 24/7 \u2022 r\u00e9ponse sous 2 h.' : 'Booking 24/7 \u2022 reply within 2 h.'}</p>
          </div>
          <Link to="/contact">
            <Button className="bg-white text-black hover:bg-white/90 rounded-none h-12 px-8 text-sm tracking-[0.2em] uppercase">
              {lang === 'fr' ? 'R\u00e9server maintenant' : 'Book now'}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Disneyland;
